import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AgentTool } from '@/redux/internal-services/agentsApi';
import { AgentFormValues, SectionCard, Toggle, JsonField, inputCls, labelCls } from './agentShared';
import { cn } from '@/utils/cn';

interface ToolsTabProps {
  allTools: AgentTool[];
}

// ── Enabled tool accordion row ────────────────────────────────────────────────

const EnabledToolRow: React.FC<{
  tool: AgentTool;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onUpdate: (patch: Partial<AgentTool>) => void;
}> = ({ tool, expanded, onToggleExpand, onRemove, onUpdate }) => (
  <div className="border border-[#EDEDED] dark:border-white/10 rounded-lg overflow-hidden">
    {/* compact header */}
    <div
      className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-ui-hover transition-colors select-none"
      onClick={onToggleExpand}
    >
      <span className="text-ui-text3 text-xs w-3 shrink-0">{expanded ? '▾' : '▸'}</span>
      <span className="font-mono text-sm text-ui-text flex-1 truncate min-w-0">
        {tool.name || tool.tool_name}
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        {tool.toolkit_name && (
          <span className="hidden sm:inline-flex text-[10px] text-ui-text3 border border-[#EDEDED] dark:border-white/10 rounded px-1.5 py-0.5">
            {tool.toolkit_name}
          </span>
        )}
        {tool.requires_confirmation && (
          <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-100 rounded px-1.5 py-0.5">
            confirm
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-xs text-[#CF4148] hover:underline ml-1"
        >
          Remove
        </button>
      </div>
    </div>

    {/* expandable editor */}
    {expanded && (
      <div className="border-t border-[#EDEDED] dark:border-white/10 px-3 pb-4 pt-3 space-y-3 bg-gray-50/50 dark:bg-gray-800/50">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              Actual tool name <span className="text-ui-text3 font-normal">— fixed</span>
            </label>
            <div className={cn(inputCls, 'bg-ui-subtle font-mono text-ui-text2 cursor-default')}>
              {tool.name || tool.tool_name}
            </div>
          </div>
          <div>
            <label className={labelCls}>
              Agent name <span className="text-ui-text3 font-normal">— what this agent calls it</span>
            </label>
            <input
              className={cn(inputCls, 'font-mono')}
              value={tool.tool_name ?? ''}
              onChange={(e) => onUpdate({ tool_name: e.target.value.replace(/\s+/g, '_') })}
              placeholder={tool.name}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>
            Description <span className="text-ui-text3 font-normal">— shown to the model</span>
          </label>
          <textarea
            className={inputCls}
            rows={2}
            value={tool.description ?? ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="What this tool does and when to use it"
          />
        </div>

        <div>
          <label className={labelCls}>
            Argument schema <span className="text-ui-text3 font-normal">— JSON schema of the tool's inputs</span>
          </label>
          <JsonField
            value={tool.argument_schema ?? { type: 'object', properties: {} }}
            onChange={(v) => onUpdate({ argument_schema: v as Record<string, unknown> })}
            rows={5}
            placeholder='{"type": "object", "properties": {}}'
          />
        </div>

        <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
          <Toggle
            on={!!tool.requires_confirmation}
            onChange={() => onUpdate({ requires_confirmation: !tool.requires_confirmation })}
          />
          Require user confirmation before running
        </label>

        {tool.requires_confirmation && (
          <div>
            <label className={labelCls}>
              Confirmation card template <span className="text-ui-text3 font-normal">— optional JSON</span>
            </label>
            <JsonField
              value={tool.action_card_template ?? {}}
              onChange={(v) => onUpdate({ action_card_template: v })}
              rows={3}
              placeholder='{"title": "Confirm action", "fields": []}'
            />
          </div>
        )}
      </div>
    )}
  </div>
);

// ── Catalogue toolkit group ───────────────────────────────────────────────────

const ToolkitGroup: React.FC<{
  name: string;
  tools: AgentTool[];
  enabledIds: Set<string | undefined>;
  onAdd: (t: AgentTool) => void;
  forceOpen?: boolean;
}> = ({ name, tools, enabledIds, onAdd, forceOpen }) => {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  const addedCount = tools.filter((t) => enabledIds.has(t.tool_id)).length;

  return (
    <div className="border border-[#EDEDED] dark:border-white/10 rounded-lg overflow-hidden">
      {/* toolkit header */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-ui-hover transition-colors select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-ui-text3 text-xs w-3 shrink-0">{isOpen ? '▾' : '▸'}</span>
        <span className="text-sm font-medium text-ui-text flex-1 min-w-0 truncate">{name}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {addedCount > 0 && (
            <span className="text-[10px] bg-brand-light text-brand-dark rounded px-1.5 py-0.5 font-medium">
              {addedCount}/{tools.length} added
            </span>
          )}
          {addedCount === 0 && (
            <span className="text-[10px] text-ui-text3">{tools.length} tool{tools.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* tool rows inside toolkit */}
      {isOpen && (
        <div className="border-t border-[#EDEDED] dark:border-white/10 divide-y divide-[#EDEDED] dark:divide-white/10">
          {tools.map((t) => {
            const added = enabledIds.has(t.tool_id);
            return (
              <div key={t.tool_id} className="flex items-center gap-3 px-4 py-2.5 bg-gray-50/40 dark:bg-gray-800/40">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ui-text font-mono">{t.name || t.tool_name}</span>
                    {t.requires_confirmation && (
                      <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-100 rounded px-1.5 py-0.5">
                        confirm
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-ui-text3 mt-0.5 line-clamp-1">{t.description}</p>
                  )}
                </div>
                {added ? (
                  <span className="text-xs text-emerald-600 font-medium shrink-0">Added</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAdd(t)}
                    className="text-sm text-brand-dark font-medium hover:underline shrink-0"
                  >
                    + Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main ToolsTab ─────────────────────────────────────────────────────────────

const ToolsTab: React.FC<ToolsTabProps> = ({ allTools }) => {
  const { watch, setValue } = useFormContext<AgentFormValues>();
  const selectedTools = watch('tools') || [];
  const selectedIds = new Set(selectedTools.map((t) => t.tool_id));

  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  const toggleExpand = (id: string | undefined) =>
    setExpandedId((prev) => (prev === id ? undefined : id));

  const addTool = (t: AgentTool) => {
    setValue(
      'tools',
      [
        ...selectedTools,
        {
          tool_id: t.tool_id,
          name: t.name,
          tool_name: t.tool_name || t.name,
          description: t.description,
          requires_confirmation: t.requires_confirmation ?? false,
          argument_schema: t.argument_schema,
          toolkit_type: t.toolkit_type,
          toolkit_name: t.toolkit_name,
          action_card_template: t.action_card_template,
        },
      ],
      { shouldDirty: true }
    );
  };

  const removeTool = (toolId: string | undefined) => {
    setValue(
      'tools',
      selectedTools.filter((t) => t.tool_id !== toolId),
      { shouldDirty: true }
    );
    if (expandedId === toolId) setExpandedId(undefined);
  };

  const updateTool = (toolId: string | undefined, patch: Partial<AgentTool>) =>
    setValue(
      'tools',
      selectedTools.map((t) => (t.tool_id === toolId ? { ...t, ...patch } : t)),
      { shouldDirty: true }
    );

  // group catalogue tools by toolkit_name (ungrouped → "Other")
  const toolkitGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? allTools.filter(
          (t) =>
            (t.name || t.tool_name || '').toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q) ||
            (t.toolkit_name || '').toLowerCase().includes(q)
        )
      : allTools;

    const map = new Map<string, AgentTool[]>();
    for (const t of filtered) {
      const key = t.toolkit_name || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    // sort: named toolkits first, "Other" last
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
  }, [allTools, search]);

  return (
    <>
      {/* ── Enabled tools ── */}
      <SectionCard
        title="Enabled tools"
        desc="Click a tool to edit its name, description, schema, and confirmation behaviour."
      >
        {selectedTools.length === 0 ? (
          <p className="text-xs text-ui-text3">No tools enabled yet. Add one from the catalogue below.</p>
        ) : (
          <div className="space-y-2">
            {selectedTools.map((t) => (
              <EnabledToolRow
                key={t.tool_id}
                tool={t}
                expanded={expandedId === t.tool_id}
                onToggleExpand={() => toggleExpand(t.tool_id)}
                onRemove={() => removeTool(t.tool_id)}
                onUpdate={(patch) => updateTool(t.tool_id, patch)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Tool catalogue ── */}
      <SectionCard
        title="Tool catalogue"
        desc="Tools are grouped by toolkit. Expand a toolkit to add tools to this agent."
      >
        {/* search */}
        <div className="mb-3">
          <input
            className={inputCls}
            placeholder="Search tools or toolkits…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {toolkitGroups.length === 0 ? (
          <p className="text-xs text-ui-text3">No tools match your search.</p>
        ) : (
          <div className="space-y-2">
            {toolkitGroups.map(([name, tools]) => (
              <ToolkitGroup
                key={name}
                name={name}
                tools={tools}
                enabledIds={selectedIds}
                onAdd={addTool}
                forceOpen={search.trim().length > 0}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default ToolsTab;
